import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Avatar,
  Badge,
  Button,
  Flex,
  LoadingOverlay,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import type { BadgeProps } from "@mantine/core";

import {
  getCompanyPhoto,
  updateCompanyById,
  uploadCompanyPhoto,
} from "../api/companyApi";
import { useAuth } from "../context/AuthContext";
import { saveNotification, updateNotification } from "../utils/useNotification";

import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import { useForm } from "@mantine/form";
import { validateRequired, validateSchedule } from "../utils/validations";
import { getLocalTimeString } from "../utils/dateFormats";
import CustomSearchInput from "../components/common/Inputs/CustomSearchInput";
import dayjs from "dayjs";
import WeeklySchedule from "../components/common/WeeklyWorkTimePlan";
import { normalizeWorkHours } from "../utils/formatWorkHours";
import PlusIcon from "../components/common/Icons/PlusIcon";
import DocumentsInput from "../components/common/Inputs/DocumentsInput";

const GeneralSettings = () => {
  const { company, setCompany } = useAuth();

  const formRef = useRef<HTMLFormElement>(null);

  // 0 = Pazar, 6 = Cumartesi
  const defaultWorkHours = Array(7)
    .fill(null)
    .reduce((acc, _, index) => {
      acc[index] = {
        enabled: false,
        start: "",
        duration: 0,
        end: "",
      };
      return acc;
    }, {} as Record<number, any>);

  const [photos, setPhotos] = useState<{ [key: string]: File | null }>({
    hr: null,
    mobile: null,
    terminal: null,
  });
  const [previews, setPreviews] = useState<{ [key: string]: string | null }>({
    hr: null,
    mobile: null,
    terminal: null,
  });
  const [images, setImages] = useState<{ [key: string]: string | null }>({
    hr: null,
    mobile: null,
    terminal: null,
  });
  const [loadingState, setLoadingState] = useState<{ [key: string]: boolean }>({
    hr: true,
    mobile: true,
    terminal: true,
  });
  const [overlayVisible, setOverlayVisible] = useState<{
    [key: string]: boolean;
  }>({
    hr: false,
    mobile: false,
    terminal: false,
  });

  const form = useForm({
    initialValues: {
      name: company?.name as any,
      entryBuffer: company?.entryBuffer,
      exitBuffer: company?.exitBuffer,
      workHours: company?.workHours,
      documents: company?.documents,
    },
    validate: {
      name: validateRequired,
      entryBuffer: validateRequired,
      exitBuffer: validateRequired,
      workHours: validateSchedule,
    },
  });

  const uploadAllImages = async () => {
    try {
      setLoadingState((prev) => ({
        ...prev,
        hr: !!photos.hr,
        mobile: !!photos.mobile,
        terminal: !!photos.terminal,
      }));

      const uploadedPhotos = { ...images };
      const updatedCompany: any = { ...company };

      for (const type of Object.keys(photos)) {
        const photo = photos[type];
        if (!photo) continue;

        const formData = new FormData();
        formData.append("companyId", company?._id || "");
        formData.append("file", photo);

        try {
          const { data }: any = await uploadCompanyPhoto(formData, type);
          const fileId = data?.fileId;
          if (fileId) {
            switch (type) {
              case "mobile":
                updatedCompany.mobilePhoto = fileId;
                break;
              case "hr":
                updatedCompany.hrPhoto = fileId;
                break;
              case "terminal":
                updatedCompany.terminalPhoto = fileId;
                break;
              default:
                break;
            }
            uploadedPhotos[type] = URL.createObjectURL(photo);
          }
        } catch (error) {
          console.error(`Upload error for ${type}:`, error);
        }
      }

      setCompany(updatedCompany);
      setImages(uploadedPhotos);
      setPhotos({ hr: null, mobile: null, terminal: null });
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoadingState({ hr: false, mobile: false, terminal: false });
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setPhotos((prev) => ({ ...prev, [type]: file }));
      const previewURL = URL.createObjectURL(file);
      setPreviews((prev) => ({ ...prev, [type]: previewURL }));
    }
  };

  const getPhoto = async (photoId: string | null, type: string) => {
    try {
      if (!photoId) return;
      setLoadingState((prev) => ({ ...prev, [type]: true }));
      const response: any = await getCompanyPhoto(photoId, type);
      const imgUrl = URL.createObjectURL(response.data);
      setImages((prev) => ({ ...prev, [type]: imgUrl }));
    } catch (error) {
      console.error(`Fetch error for ${type}:`, error);
    } finally {
      setLoadingState((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    getPhoto(company?.hrPhoto, "hr");
    getPhoto(company?.mobilePhoto, "mobile");
    getPhoto(company?.terminalPhoto, "terminal");
    // eslint-disable-next-line
  }, [company]);

  const updateCompany = async (updates: any) => {
    try {
      const { data }: any = await updateCompanyById({
        updates: updates,
      });
      if (data?.success) {
        setCompany((prev: any) => ({
          ...prev,
          ...updates,
        }));
      }
      updateNotification(data?.success);
    } catch (error) {
      console.error("Error:", error);
    } finally {
    }
  };

  useEffect(() => {
    if (company) {
      form.setValues({
        name: company.name,
        entryBuffer: company.entryBuffer ?? "",
        exitBuffer: company.exitBuffer ?? "",
        workHours: normalizeWorkHours(company.workHours),
      });
    }
  }, [company]);

  const handleSubmit = async (values: any) => {
    function timeStrToLocalDateTime(timeStr: any): any {
      const today = dayjs();
      const [hour, minute] = timeStr.split(":").map(Number);
      return today.hour(hour).minute(minute).second(0).millisecond(0).toDate();
    }

    const transformed = Object.entries(values.workHours).reduce(
      (acc, [key, value]: any) => {
        if (value.start && value.end) {
          acc[key] = {
            start: timeStrToLocalDateTime(value.start) || undefined,
            end: timeStrToLocalDateTime(value.end) || undefined,
          };
        } else {
          acc[key] = {};
        }
        return acc;
      },
      {} as Record<string, { start?: string; end?: string }>
    );

    const updatedValues: any = {
      ...values,
      entryBuffer: parseInt(values.entryBuffer),
      exitBuffer: parseInt(values.exitBuffer),
      workHours: transformed,
    };
    await uploadAllImages();
    await updateCompany(updatedValues);
    setPreviews({
      hr: null,
      mobile: null,
      terminal: null,
    });
    form.resetDirty();
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Genel Ayarlar"
        isButtoned
        buttonText="Kaydet"
        buttonIcon={<PlusIcon />}
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />
      <Wrapper>
        <form
          ref={formRef}
          onSubmit={form.onSubmit(handleSubmit)}
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            gap: "40px",
          }}
        >
          <Flex align="start" mah={"260px"} gap={40}>
            <Flex
              flex={2}
              style={{
                height: "-webkit-fill-available",
              }}
              gap={40}
            >
              {["hr", "mobile", "terminal"].map((type) => (
                <Flex
                  direction="column"
                  align="start"
                  justify="start"
                  pos="relative"
                  key={type}
                  w={"100%"}
                >
                  <InputLabel>
                    {type === "hr"
                      ? "Şirket Logosu Yükle"
                      : type === "mobile"
                      ? "Mobil Logo Yükle"
                      : type === "terminal"
                      ? "Terminal Logo Yükle"
                      : ""}
                  </InputLabel>
                  <FileInput
                    id={`${type}-file-upload`}
                    type="file"
                    onChange={(e) => handleFileChange(e, type)}
                  />
                  <FileLabel
                    htmlFor={`${type}-file-upload`}
                    onMouseEnter={() =>
                      setOverlayVisible((prev) => ({ ...prev, [type]: true }))
                    }
                    onMouseLeave={() =>
                      setOverlayVisible((prev) => ({ ...prev, [type]: false }))
                    }
                  >
                    <LoadingOverlay
                      visible={loadingState[type]}
                      zIndex={1000}
                      overlayProps={{ radius: "sm", blur: 2 }}
                    />
                    {previews[type] || images[type] ? (
                      <PhotoPreview
                        src={previews[type] || images[type] || ""}
                        alt={`${type} preview`}
                        height={"100%"}
                        width={"100%"}
                        style={{ objectFit: "contain", borderRadius: "8px" }}
                        $isChanged={photos[type] && true}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "10px",
                          border: "2px dashed #5C26FF",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          boxShadow: "0px 0px 20px 0px #00000026",
                        }}
                      >
                        <Badge
                          bg={"#5C26FF"}
                          style={{
                            cursor: "pointer",
                          }}
                          circle
                          size="40"
                        >
                          <Avatar src={"/assets/plusIcon.png"} size={28} />
                        </Badge>
                      </div>
                    )}
                    {(overlayVisible[type] &&
                      (images[type] || previews[type])) ||
                    photos[type] ? (
                      <UploadIcon circle>
                        <Avatar
                          src={"/assets/uploadIcon.png"}
                          size={24}
                          radius={0}
                        />
                      </UploadIcon>
                    ) : null}
                  </FileLabel>
                </Flex>
              ))}
            </Flex>
            <Flex flex={1} direction="column" justify={"space-between"}>
              <CustomSearchInput
                label="Şirket İsmi"
                leftSection={false}
                w="100%"
                {...form.getInputProps("name")}
              />
              <CustomSearchInput
                label="Giriş Toleransı"
                w="100%"
                leftSection={false}
                {...form.getInputProps("entryBuffer")}
              />
              <CustomSearchInput
                label="Çıkış Toleransı"
                w="100%"
                leftSection={false}
                {...form.getInputProps("exitBuffer")}
              />
            </Flex>
            {company?.modules?.isDocumentUploadAvailable && (
              <Flex flex={1}>
                <DocumentsInput form={form} />
              </Flex>
            )}
          </Flex>
          {company?.modules?.isGeneralSettingsWorkHoursAvailable && (
            <Flex flex={1}>
              <WeeklySchedule
                value={form.values?.workHours}
                onChange={(updatedObject: any) => {
                  form.setFieldValue("workHours", updatedObject);
                }}
                error={form.errors.workHours}
              />
            </Flex>
          )}

          {/* 
            <StyledButton
              fullWidth
              disabled={
                !form.isDirty() &&
                !previews["hr"] &&
                !previews["mobile"] &&
                !previews["terminal"]
              }
              type="submit"
            >
              Kaydet
            </StyledButton> */}
        </form>
      </Wrapper>
    </Container>
  );
};

export default GeneralSettings;
const Wrapper = styled(ScrollArea)`
  display: flex;
  flex-direction: column;
  background-color: white;
`;

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  line-height: 17.64px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
    width: 100%;
    height: 100%;
    position: relative;
    display: inline-block;
    margin-top: 10px;
    cursor: pointer;
    }
  `;

const PhotoPreview = styled.img<{ $isChanged: any }>`
  border: 2px solid
    ${(props): any => (props.$isChanged ? "#5C26FF" : "#d9d9d9")};
  border-radius: 8px;
  transition: border-color 0.3s ease;
  &:hover {
    box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.15);
  }
`;

const UploadIcon = styled(Badge)<BadgeProps>`
  position: absolute;
  width: 40px;
  height: 40px;
  top: -20px;
  right: -20px;
  background: #5c26ff;
  color: white;
  font-size: 20px;
  font-weight: bold;
  z-index: 10;
  cursor: pointer;
`;
